import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from './CurrentUserContext';

const ContactsContext = createContext(null);

export const useContacts = () => {
  const context = useContext(ContactsContext);
  if (!context) throw new Error('useContacts must be used within a ContactsProvider');
  return context;
};

export const ContactsProvider = ({ children }) => {
  const { currentUser, activeWorkspaceId } = useCurrentUser();
  const [contacts, setContacts] = useState([]);
  // AN EMPTY LIST AND A FAILED LOAD ARE OPPOSITE THINGS (device sitting, 2026-10-04).
  //
  // Carla: "wifi off, opened a list screen: IT SHOWED AS 'no tasks'." The same shape was
  // here: a failed fetch logged to the console and left `contacts` at [], so the screen
  // said she had no contacts. This one WAS one of the 62 reads deliberately parked on
  // 2026-10-03 — "an empty list is visible but unexplained" — and it is unparked now
  // because she walked into it. The other parked reads stay parked; this is not the whole
  // sweep, and saying otherwise would make the next count worth less.
  //
  // `loaded` exists so the UI can tell "not fetched yet" from "fetched and empty". Without
  // it the first paint of a working account also reads as empty.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // TEAM-1b: what the server says this person may do with the business's contacts. Deleting is
  // owner and admin only (decision 23); everyone else is not shown a Delete that would refuse.
  const [canDeleteContacts, setCanDeleteContacts] = useState(true);

  const refresh = useCallback(() => {
    api('/api/contacts')
      .then(res => res.json())
      .then(data => {
        // A 200 CARRYING NO ARRAY IS A FAILURE, not an empty address book. The old code
        // dropped a non-array body silently and left the previous list in place.
        if (Array.isArray(data.contacts)) {
          setContacts(data.contacts);
          setCanDeleteContacts(data.permissions?.deleteContacts !== false);
          setLoadFailed(false);
        } else {
          setLoadFailed(true);
        }
        setLoaded(true);
      })
      .catch(err => {
        console.error('Failed to load contacts:', err);
        setLoadFailed(true);
        setLoaded(true);
      });
  }, []);

  useEffect(() => {
    // Not before the workspace is known (Carla, 2026-10-11): fetched without one, this ran twice on
    // every load, once for no workspace and again when it arrived.
    if (!currentUser?.id || !activeWorkspaceId) return;
    refresh();
  }, [currentUser?.id, activeWorkspaceId, refresh]);

  useEffect(() => {
    window.addEventListener('aidey:contactsUpdated', refresh);
    return () => window.removeEventListener('aidey:contactsUpdated', refresh);
  }, [refresh]);

  return (
    <ContactsContext.Provider value={{ contacts, setContacts, refresh, loadFailed, loaded, canDeleteContacts }}>
      {children}
    </ContactsContext.Provider>
  );
};
